'use client';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Theme context — `light` | `dark` | `system` (follows OS preference).
 *
 * Persists to localStorage so the choice survives refreshes. Listens to
 * the OS-level `prefers-color-scheme` media query so `system` mode reacts
 * live when the user flips their OS theme.
 *
 * Applies the active mode by adding/removing `class="dark"` on <html>,
 * matching Tailwind's `darkMode: 'class'` strategy.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  preference: ThemePreference;
  /** The resolved mode actually applied right now (system → light|dark). */
  effective: 'light' | 'dark';
  setPreference: (p: ThemePreference) => void;
}

const STORAGE_KEY = 'flowstage_theme_pref';

const ThemeContext = createContext<ThemeContextValue>({
  preference: 'system',
  effective: 'light',
  setPreference: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Track OS-level changes so `system` mode reacts live.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const effective: 'light' | 'dark' =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  // Apply / strip the `dark` class on <html>.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (effective === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [effective]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, p);
    }
  }, []);

  return (
    <ThemeContext.Provider data-component-id="src/contexts/ThemeContext.tsx:83:4" data-component-path="src/contexts/ThemeContext.tsx" data-component-line="83" data-component-end-line="85" data-component-file="ThemeContext.tsx" data-component-name="ThemeContext.Provider" data-component-content="%7B%22elementName%22%3A%22ThemeContext.Provider%22%7D" value={{ preference, effective, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}
